import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import { PifListFilters, usePersistedPifFilters } from "../../hooks/usePersistedPifFilters";
import { useToaster } from "../../hooks/useToaster";
import { useMenu } from "../../context/MenuContext";
import { useUser } from "../../context/UserContext";
import { ModuleNames, SEARCHBOX, SEVERITIES } from "../../data/constants";
import { POSTIF_TEXT } from "../../constants/messages";
import SearchBox from "../../components/form/SearchBox/SearchBox";
import { getMyPostInterviewForms, getPostInterviewForms, markPostIfAsEmployee, softDeletePostIf } from "../../services/pifService";
import {
  assignPostIfCoordinator,
  getPifDepartments,
  getPostIfHrCoordinators,
  HR_LIST_PAGE_SIZE,
  PifHrListServerFilters,
  pifLastChangedNow,
  sortPifHrBacklog,
  toPifApiStatus,
} from "../../services/pifListApproval";
import { PostIfLookupItem } from "../../types/pifReferenceDto";
import {
  PIF_LIST_STATUS_LABEL,
  PIF_LIST_STATUS_ORDER,
  PifListItem,
  PifListStatus,
} from "../../pages/supervision/PostInterviewForm/types";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import PostIFMarkEmployeeDialog from "./PostIFMarkEmployeeDialog";
import PostIFTable, { PostIFTableVariant } from "./PostIFTable";
import PostIFDetailDialog from "./PostIFDetailDialog";
import PifPageBand from "../../pages/supervision/PostInterviewForm/PifPageBand";
import { PifMenuSelect } from "../../pages/supervision/PostInterviewForm/pifSelectMenu";
import "./PostInterviewForms.scss";

const ALL = "all";
const NEW_FORM_URL = "/hiring/postif/new";

/** The coordinator filter's static "no coordinator yet" option (Epic 82838). */
const UNASSIGNED = "unassigned";

const DEFAULT_FILTERS: PifListFilters = { status: ALL, department: ALL, coordinator: ALL };
const isListStatus = (value: string) =>
  value === ALL || (PIF_LIST_STATUS_ORDER as string[]).includes(value);

const PostInterviewForms: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions } = useMenu();
  const { userId } = useUser();
  // Its own useApi instance, so only the list load drives the grid's spinner. useApi (unlike
  // useParemeterLessApi) catches a failed request, clears the spinner and alerts; a null result
  // is what flips the page into its error + Retry state (Epic 82838).
  const { loading, executeApi: executeLoad } = useApi();
  const { executeApi: executeMutation } = useApi();
  const showToaster = useToaster();
  const [allRows, setAllRows] = useState<PifListItem[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  // Kept with their ids: the filter matches rows by NAME, but the server filters by departmentId.
  const [departmentLookup, setDepartmentLookup] = useState<PostIfLookupItem[]>([]);
  const [searchText, setSearchText] = useState("");
  // Remembered per user (Epic 82838); a fresh user starts on "All".
  const {
    filters: { department, status, coordinator = ALL },
    updateFilters,
  } = usePersistedPifFilters(userId, DEFAULT_FILTERS, isListStatus);
  // Epic 82838 - HR Coordinator assignment.
  const [coordinators, setCoordinators] = useState<PostIfLookupItem[]>([]);
  const [savingCoordinatorIds, setSavingCoordinatorIds] = useState<ReadonlySet<string>>(() => new Set());
  // Set when an unfiltered HR list fills its one page: there may be more rows than were loaded, so
  // the filters and search go to the server rather than narrowing only what is here.
  const [listTruncated, setListTruncated] = useState(false);
  const [detailRow, setDetailRow] = useState<PifListItem | null>(null);
  const [deleteRow, setDeleteRow] = useState<PifListItem | null>(null);
  const [markRow, setMarkRow] = useState<PifListItem | null>(null);

  const canViewHrList = Boolean(
    permissions.find((permission) => permission.moduleName === ModuleNames.PostIFList)?.canView,
  );
  // Hiring supervisor view unless the user has the HR list grant.
  const variant: PostIFTableVariant = canViewHrList ? "hr" : "my";

  // "+ New PostIF" opens /hiring/postif/new, which MenuGuard gates on
  // EnterprisePortal-PostIF-Form (RouteKeywords "postif/" -> ModuleNames.PostIFForm). Gate the
  // button on that SAME grant rather than on which grid is showing: keyed off the grid, someone
  // with the Form grant but viewing the HR list got no button at all, while a supervisor without
  // it got a button that only ever led to the not-authorized page. The Form module is deliberately
  // separate from the two list grants (see Epic-79767_Supervision_PostIF.sql) precisely so that
  // "who may fill a form" is decided independently of "whose list you can see".
  const formGrant = permissions.find((permission) => permission.moduleName === ModuleNames.PostIFForm);
  const canCreateForm =
    // canView AND canEdit, both on the Form module. canView alone is not enough: creating a form is
    // a WRITE, and in this schema CanEdit is the create flag - Portal_ModuleRolePermissions has no
    // CanAdd column at all, which is why sProc_GetActiveRoles_Module_Permission_S resolves
    // 'CanAdd' as `PMR.CanEdit = 1`. canView is still required because MenuGuard gates the
    // /hiring/postif/new route on it, so without it the button would only lead to Not Authorized.
    Boolean(formGrant?.canView && formGrant?.canEdit);

  // Assigning needs Edit on the PostIF-List module - the same grant the API's CanAct 'mark' is
  // built on - so without it the coordinator cell is read-only rather than a dropdown that 403s.
  const canAssignCoordinator =
    variant === "hr" &&
    Boolean(permissions.find((permission) => permission.moduleName === ModuleNames.PostIFList)?.canEdit);

  // Epic 82838: "server term for large sets, client filter otherwise". Once the HR list is known
  // to hold more rows than one page loads, search AND the status / department / coordinator
  // filters go to the server - filtering only the loaded page would silently miss forms, and the
  // server returns that page in backlog order, so it holds the oldest open work (PR #32636 review).
  // The page still applies the same filters to what comes back, which also narrows 'submitted' to
  // the exact pending stage.
  const departmentIdByName = useMemo(
    () => new Map(departmentLookup.filter((item) => item.text).map((item) => [item.text as string, item.id])),
    [departmentLookup],
  );
  // Only the filters actually set, as a key: a filter change that sends nothing new (any change
  // while the list fits one page, or the department lookup landing) must not re-fetch the list.
  const serverFiltersKey = JSON.stringify(
    variant !== "hr" || !listTruncated
      ? {}
      : {
          search: searchText.trim() || undefined,
          status: status !== ALL ? toPifApiStatus(status as PifListStatus) : undefined,
          // A department known only from the rows (renamed / no lookup id) stays a page-only filter.
          departmentId: department !== ALL ? departmentIdByName.get(department) : undefined,
          unassigned: coordinator === UNASSIGNED ? true : undefined,
          hrCoordinatorUserId: coordinator !== ALL && coordinator !== UNASSIGNED ? Number(coordinator) : undefined,
        },
  );
  // JSON.stringify drops the undefined entries, so this holds only the filters that are set.
  const serverFilters = useMemo<PifHrListServerFilters>(() => JSON.parse(serverFiltersKey), [serverFiltersKey]);
  const isServerFiltered = Object.keys(serverFilters).length > 0;
  const serverSearch = serverFilters.search ?? "";

  // "My" and "HR" are separate scoped endpoints (my-list / hr-list); the server decides the
  // scope, so we call the one matching the grid variant rather than filtering client-side.
  const loadList = useCallback(async () => {
    const fetchList = (filters: PifHrListServerFilters) =>
      variant === "hr" ? getPostInterviewForms(userId, filters) : getMyPostInterviewForms(userId);
    const data = await executeLoad(fetchList, serverFilters);
    setLoadFailed(!data);
    if (data) {
      setAllRows(data);
      // Only an unfiltered load says how big the list really is.
      if (!isServerFiltered) setListTruncated(variant === "hr" && data.length >= HR_LIST_PAGE_SIZE);
    }
  }, [executeLoad, variant, userId, serverFilters, isServerFiltered]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // The department options come from the /postif/departments master list, NOT from the loaded
  // rows - a department with no form yet must still be selectable. This is deliberately outside
  // useParemeterLessApi: that hook owns the grid's spinner, and a lookup call must not flip the
  // table into a loading state. A lookup failure is swallowed so the page keeps working; the
  // row-derived names below still populate the dropdown.
  useEffect(() => {
    let cancelled = false;
    void getPifDepartments()
      .then((items) => {
        if (!cancelled) setDepartmentLookup(items.filter((item) => item.text));
      })
      .catch(() => {
        if (!cancelled) setDepartmentLookup([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // The coordinator picker's options - HR list only. Like the department lookup, a failure leaves
  // the list working: cells then offer only "Unassigned" plus whoever a row already names.
  useEffect(() => {
    if (variant !== "hr") return undefined;
    let cancelled = false;
    void getPostIfHrCoordinators()
      .then((items) => {
        if (!cancelled) setCoordinators(items);
      })
      .catch(() => {
        if (!cancelled) setCoordinators([]);
      });
    return () => {
      cancelled = true;
    };
  }, [variant]);

  // Filter options: the pool plus anyone a row is still assigned to who has since left the role.
  const coordinatorFilterOptions = useMemo(() => {
    const byId = new Map<number, string>();
    coordinators.forEach((item) => byId.set(item.id, item.text ?? `User ${item.id}`));
    allRows.forEach((row) => {
      if (row.hrCoordinatorUserId !== undefined && !byId.has(row.hrCoordinatorUserId)) {
        byId.set(row.hrCoordinatorUserId, row.hrCoordinatorName ?? `User ${row.hrCoordinatorUserId}`);
      }
    });
    return Array.from(byId, ([id, text]) => ({ id, text })).sort((a, b) => a.text.localeCompare(b.text));
  }, [coordinators, allRows]);

  const setRowCoordinator = useCallback((rowId: string, coordinatorUserId?: number, coordinatorName?: string) => {
    setAllRows((rows) =>
      rows.map((row) =>
        row.id === rowId ? { ...row, hrCoordinatorUserId: coordinatorUserId, hrCoordinatorName: coordinatorName } : row,
      ),
    );
  }, []);

  const setCoordinatorSaving = useCallback((rowId: string, saving: boolean) => {
    setSavingCoordinatorIds((current) => {
      const next = new Set(current);
      if (saving) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);

  // Epic 82838 - optimistic, last-write-wins: the cell shows the new coordinator at once; a failed
  // save puts the previous one back and says so. useApi has already logged the failure.
  const handleAssignCoordinator = useCallback(
    async (row: PifListItem, coordinatorUserId: number | null) => {
      if ((row.hrCoordinatorUserId ?? null) === coordinatorUserId) return;

      const previous = { id: row.hrCoordinatorUserId, name: row.hrCoordinatorName };
      const name =
        coordinatorUserId === null
          ? undefined
          : coordinatorFilterOptions.find((option) => option.id === coordinatorUserId)?.text;

      setRowCoordinator(row.id, coordinatorUserId ?? undefined, name);
      setCoordinatorSaving(row.id, true);
      const result = await executeMutation(assignPostIfCoordinator, {
        postIfId: Number(row.id),
        hrCoordinatorUserId: coordinatorUserId,
      });
      setCoordinatorSaving(row.id, false);

      if (result) {
        // The sproc stamped UpdatedUtc; mirror it so "Last changed" and the backlog order are right
        // without re-reading the list (PR #32635 review).
        const changed = pifLastChangedNow();
        setAllRows((rows) => rows.map((item) => (item.id === row.id ? { ...item, ...changed } : item)));
        showToaster(
          coordinatorUserId === null ? POSTIF_TEXT.COORDINATOR_CLEARED : POSTIF_TEXT.COORDINATOR_ASSIGNED(name ?? "the coordinator"),
          SEVERITIES.SUCCESS,
        );
      } else {
        setRowCoordinator(row.id, previous.id, previous.name);
        showToaster(POSTIF_TEXT.COORDINATOR_ASSIGN_FAILED, SEVERITIES.ERROR);
      }
    },
    [coordinatorFilterOptions, executeMutation, setCoordinatorSaving, setRowCoordinator, showToaster],
  );

  // Master list unioned with whatever the rows carry, so a department that was renamed or
  // deactivated but still has records stays filterable. The filter matches on NAME
  // (PifListItem.department is the name); the lookup's id is only used for the server filter.
  const departments = useMemo(() => {
    const names = new Set(
      [...departmentLookup.map((item) => item.text ?? ""), ...allRows.map((row) => row.department)].filter(Boolean),
    );
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [departmentLookup, allRows]);

  const visibleRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    // Epic 82838: the Employee ID matches too, partially and with or without the "#" the grid
    // shows ("1002", "#100234"). Only the id is matched, never SSN or any other identifier.
    const employeeIdSearch = search.replace(/^#/, "");

    const filtered = allRows.filter((row) => {
      if (department !== ALL && row.department !== department) return false;
      if (status !== ALL && row.status !== status) return false;
      if (variant === "hr" && coordinator !== ALL) {
        const matches =
          coordinator === UNASSIGNED
            ? row.hrCoordinatorUserId === undefined
            : String(row.hrCoordinatorUserId) === coordinator;
        if (!matches) return false;
      }
      // A server-searched load has already matched the term (title, creator, applicant name or
      // Employee ID) - filtering it again here would drop the creator-name matches.
      if (!search || serverSearch) return true;

      if (
        employeeIdSearch &&
        row.enterpriseEmployeeId !== undefined &&
        String(row.enterpriseEmployeeId).includes(employeeIdSearch)
      ) {
        return true;
      }
      return [row.title, row.applicant, row.position].some((field) =>
        field.toLowerCase().includes(search),
      );
    });
    // HR works the list as a backlog; the supervisor's own list keeps the server's order.
    return variant === "hr" ? sortPifHrBacklog(filtered) : filtered;
  }, [allRows, department, status, coordinator, searchText, serverSearch, variant]);

  const handleNewForm = () => navigate(NEW_FORM_URL);

  // Carry the ?view= override onto the detail routes so the list crumb can return to the
  // same grid — without it the list falls back to permissions and shows the supervisor view.
  const listSearch = searchParams.toString();
  const withListSearch = (path: string) => (listSearch ? `${path}?${listSearch}` : path);

  const handleOpenFromDetail = (row: PifListItem) => {
    setDetailRow(null);
    // "Open form" only OPENS the record - it never starts an edit, on either grid. The plain
    // /:id route is the read-only approval screen (sign buttons appear only when it's the
    // viewer's turn), so a draft or a returned form is shown as-is rather than reopened in the
    // editor. Editing stays on the pencil action in PostIFTable, which is already gated to the
    // rows the viewer may actually mutate; routing an editable row here too meant one click from
    // the lightbox silently put the form back into edit mode.
    navigate(withListSearch(`/hiring/postif/${row.id}`));
  };

  // HR clicks "Mark as employee" → collect the Enterprise employee number, then call the API and
  // refetch (mark-as-employee stamps EnterpriseEmployeeId, which drops the row off the HR list).
  const handleConfirmMark = async (enterpriseEmployeeId: number) => {
    if (!markRow) return;
    const result = await executeMutation(markPostIfAsEmployee, {
      postIfId: Number(markRow.id),
      enterpriseEmployeeId,
    });
    if (result) {
      showToaster("Marked as employee.", SEVERITIES.SUCCESS);
      await loadList();
    }
    setMarkRow(null);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    if (confirmed && deleteRow) {
      // useApi surfaces any business failure (403 not-owner, 409 already-submitted, …) as a
      // Redux error alert and returns null, so a non-null result means the delete succeeded.
      const result = await executeMutation(softDeletePostIf, Number(deleteRow.id));
      if (result) {
        showToaster("Draft deleted.", SEVERITIES.SUCCESS);
        await loadList();
      }
    }
    setDeleteRow(null);
  };

  const handleDepartmentChange = (event: SelectChangeEvent<string>) => {
    updateFilters({ department: event.target.value });
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    updateFilters({ status: event.target.value });
  };

  const handleCoordinatorFilterChange = (event: SelectChangeEvent<string>) => {
    updateFilters({ coordinator: event.target.value });
  };

  return (
    <div className="postif-page">
      {/* No `current`: this page IS the list, so its own crumb ends the trail. */}
      <PifPageBand title="Post Interview List" />

      <section className="postif-card">
        <div className="postif-card__toolbar">
          <Box className="search-box-wrap postif-card__search-wrap" data-testid="postif-search">
            <SearchBox
              searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
              onSearch={setSearchText}
            />
          </Box>

          <FormControl className="postif-card__select" size="small">
            <InputLabel id="postif-department-label">Department</InputLabel>
            <PifMenuSelect
              labelId="postif-department-label"
              id="postif-department-select"
              label="Department"
              value={department}
              onChange={handleDepartmentChange}
              data-testid="postif-filter-dept"
            >
              <MenuItem value={ALL}>All departments</MenuItem>
              {departments.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </PifMenuSelect>
          </FormControl>

          <FormControl className="postif-card__select" size="small">
            <InputLabel id="postif-status-label">Status</InputLabel>
            <PifMenuSelect
              labelId="postif-status-label"
              id="postif-status-select"
              label="Status"
              value={status}
              onChange={handleStatusChange}
              data-testid="postif-filter-status"
            >
              <MenuItem value={ALL}>All statuses</MenuItem>
              {/* Epic 82838: the five stages first, then Draft / Returned / Declined. */}
              {PIF_LIST_STATUS_ORDER.map((option) => (
                <MenuItem key={option} value={option}>
                  {PIF_LIST_STATUS_LABEL[option]}
                </MenuItem>
              ))}
            </PifMenuSelect>
          </FormControl>

          {/* Epic 82838 - HR list only: who is working the form, with a static "Unassigned". */}
          {variant === "hr" && (
            <FormControl className="postif-card__select" size="small">
              <InputLabel id="postif-coordinator-label">HR Coordinator</InputLabel>
              <PifMenuSelect
                labelId="postif-coordinator-label"
                id="postif-coordinator-select"
                label="HR Coordinator"
                value={coordinator}
                onChange={handleCoordinatorFilterChange}
                data-testid="postif-filter-coordinator"
              >
                <MenuItem value={ALL}>All coordinators</MenuItem>
                <MenuItem value={UNASSIGNED}>Unassigned</MenuItem>
                {coordinatorFilterOptions.map((option) => (
                  <MenuItem key={option.id} value={String(option.id)}>
                    {option.text}
                  </MenuItem>
                ))}
              </PifMenuSelect>
            </FormControl>
          )}

          {canCreateForm && (
            <Button
              className="postif-card__new"
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleNewForm}
              data-testid="postif-new"
            >
              + New PostIF
            </Button>
          )}
        </div>

        {loadFailed && !loading && (
          <Alert
            severity="error"
            className="postif-card__error"
            data-testid="postif-load-error"
            action={
              <Button color="inherit" size="small" onClick={loadList} data-testid="postif-load-retry">
                Retry
              </Button>
            }
          >
            {POSTIF_TEXT.LIST_LOAD_ERROR}
          </Alert>
        )}

        <PostIFTable
          // The grid captures its column set once (useResizableColumns), so a my/hr switch remounts it.
          key={variant}
          variant={variant}
          rows={visibleRows}
          loading={loading}
          coordinators={coordinatorFilterOptions}
          canAssignCoordinator={canAssignCoordinator}
          savingCoordinatorIds={savingCoordinatorIds}
          onAssignCoordinator={handleAssignCoordinator}
          onEdit={(row) => navigate(withListSearch(`/hiring/postif/${row.id}/edit`))}
          onOpenDetail={setDetailRow}
          onOpen={handleOpenFromDetail}
          onDelete={setDeleteRow}
          onMarkAsEmployee={setMarkRow}
        />
      </section>

      <PostIFDetailDialog
        record={detailRow}
        openLabel={variant === "hr" ? "Open" : "Open form"}
        onClose={() => setDetailRow(null)}
        onOpen={handleOpenFromDetail}
      />

      <ConfirmationDialog
        open={Boolean(deleteRow)}
        title={POSTIF_TEXT.DELETE_CONFIRM_TITLE}
        message={deleteRow ? POSTIF_TEXT.DELETE_CONFIRM_MESSAGE(deleteRow.title) : ""}
        initialFocus="no"
        onClose={handleConfirmDelete}
      />

      <PostIFMarkEmployeeDialog
        open={Boolean(markRow)}
        applicantName={markRow?.applicant}
        defaultEmployeeId={markRow?.enterpriseEmployeeId}
        onClose={() => setMarkRow(null)}
        onConfirm={handleConfirmMark}
      />
    </div>
  );
};

export default PostInterviewForms;
